/**
 * Слой водоснабжения: трубы между точками слоя сантехники. Конец трубы — стояк,
 * коллектор или водорозетка на стене; к умывальнику или унитазу трубу не ведут —
 * у них нет вывода, вывод и есть водорозетка. Труба без обоих концов не создаётся,
 * а среда трубы должна подходить обеим точкам: к стояку ХВС горячую не подвести.
 */
import { create } from "zustand";
import { uid, type Pipe } from "@/shared/plan";
import { acceptsMedium, getCatalogItem, type WaterMedium } from "@/shared/catalog";
import { usePlumbingStore } from "./plumbingStore";
import type { Pt } from "@/shared/lib/geometry/geom";

export interface PipesLayerState {
	items: Record<string, Pipe>;
	visible: boolean;

	addPipe: (
		from: string,
		to: string,
		points: Pt[],
		medium: WaterMedium,
		material?: string,
	) => string | null;
	updatePipe: (id: string, patch: Partial<Omit<Pipe, "id" | "from" | "to">>) => void;

	setVisible: (visible: boolean) => void;
	toggleVisible: () => void;

	restorePipes: (pipes: Record<string, Pipe>) => void;
}

/** Есть ли такая точка на стене и принимает ли она среду. */
const acceptsPipe = (id: string, medium: WaterMedium): boolean => {
	const fixture = usePlumbingStore.getState().items[id];
	return fixture !== undefined && acceptsMedium(getCatalogItem(fixture.ref), medium);
};

export const usePipesStore = create<PipesLayerState>((set) => ({
	items: {},
	visible: true,

	addPipe: (from, to, points, medium, material) => {
		if (from === to || !acceptsPipe(from, medium) || !acceptsPipe(to, medium)) return null;

		const id = uid("t");
		const pipe: Pipe = {
			id,
			from,
			to,
			points: points.map((p) => ({ x: Math.round(p.x), y: Math.round(p.y) })),
			medium,
			...(material ? { material } : {}),
		};
		set((s) => ({
			items: { ...s.items, [id]: pipe },
			visible: true,
		}));
		return id;
	},

	updatePipe: (id, patch) =>
		set((s) => {
			const pipe = s.items[id];
			if (!pipe) return s;
			const next = { ...pipe, ...patch };
			if (!acceptsPipe(next.from, next.medium) || !acceptsPipe(next.to, next.medium)) return s;
			return { items: { ...s.items, [id]: next } };
		}),

	setVisible: (visible) => set({ visible }),
	toggleVisible: () => set((s) => ({ visible: !s.visible })),

	restorePipes: (pipes) => set({ items: pipes }),
}));
